import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SUPPORT_FAQ_SECTIONS } from '../../constants/supportFaqData';
import HelpCenterTabs from '../../components/HelpCenterTabs';
import './FaqPage.css';

const FAQ_METRICS = [
  { label: 'Tiempo de respuesta promedio', value: '2h 15m' },
  { label: 'Resolucion en el primer contacto', value: '87%' },
  { label: 'Disponibilidad de la plataforma', value: '99.9%' },
];

const buildInitialAccordionState = () =>
  SUPPORT_FAQ_SECTIONS.reduce((acc, section) => {
    acc[section.type] = false;
    return acc;
  }, {});

const filterFaqSections = (term, sections) => {
  const normalized = term.trim().toLowerCase();
  if (!normalized) return sections;

  return sections
    .map(section => ({
      ...section,
      entries: section.entries.filter(entry => {
        const question = entry.question?.toLowerCase() || '';
        const answer = entry.answer?.toLowerCase() || '';
        return question.includes(normalized) || answer.includes(normalized);
      }),
    }))
    .filter(section => section.entries.length > 0);
};

export default function FaqPage() {
  const [searchValue, setSearchValue] = useState('');
  const [openSections, setOpenSections] = useState(buildInitialAccordionState);

  const filteredFaqs = useMemo(
    () => filterFaqSections(searchValue, SUPPORT_FAQ_SECTIONS),
    [searchValue]
  );

  const toggleSection = type => {
    setOpenSections(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  return (
    <>
      <HelpCenterTabs />
      <main className="faq-page">
      <header className="faq-page__hero">
        <div className="faq-page__intro">
          <span className="faq-page__kicker">Centro de ayuda</span>
          <h1 className="faq-page__title">Preguntas frecuentes</h1>
          <p className="faq-page__subtitle">
            Encuentra respuestas concretas sobre activacion, operaciones y soporte de Flotte. Si
            necesitas acompanamiento personalizado, puedes{' '}
            <Link to="/dashboard?section=support-new">abrir un ticket en el centro de soporte</Link>.
          </p>
        </div>

        <ul className="faq-page__meta">
          {FAQ_METRICS.map(metric => (
            <li key={metric.label}>
              <span className="faq-page__meta-label">{metric.label}</span>
              <strong className="faq-page__meta-value">{metric.value}</strong>
            </li>
          ))}
        </ul>

        <div className="faq-page__search">
          <input
            type="search"
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            placeholder="Busca por palabra clave (por ejemplo: unidades, facturacion, accesos)"
          />
        </div>
      </header>

      <section className="faq-page__sections" aria-label="Listado de preguntas frecuentes">
        {filteredFaqs.length ? (
          filteredFaqs.map(section => {
            const contentId = `faq-section-${section.type}`;
            const isOpen = searchValue ? true : openSections[section.type];
            return (
              <article
                key={section.type}
                className={`faq-section${isOpen ? ' faq-section--open' : ''}`}
              >
                <button
                  type="button"
                  className="faq-section__header"
                  onClick={() => toggleSection(section.type)}
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                >
                  <div className="faq-section__copy">
                    <span className="faq-section__eyebrow">Categoria</span>
                    <div className="faq-section__title">
                      <h2>{section.title}</h2>
                      {section.description ? <p>{section.description}</p> : null}
                    </div>
                  </div>
                  <div className="faq-section__meta">
                    <span className="faq-section__count">
                      {section.entries.length} {section.entries.length === 1 ? 'articulo' : 'articulos'}
                    </span>
                    <span className="faq-section__icon" aria-hidden="true" />
                  </div>
                </button>
                <div
                  id={contentId}
                  className="faq-section__entries"
                  hidden={!isOpen}
                >
                  <ul>
                    {section.entries.map(entry => (
                      <li key={entry.question}>
                        <h3>{entry.question}</h3>
                        <p>{entry.answer}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })
        ) : (
          <p className="faq-page__empty">
            No encontramos resultados con ese termino. Intenta con otra palabra clave o{' '}
            <Link to="/dashboard?section=support-new">envianos un ticket</Link>.
          </p>
        )}
      </section>
    </main>
    </>
  );
}
